import clsx from "clsx";
import React, {
  ForwardRefRenderFunction,
  useEffect,
  useRef,
  useState,
} from "react";

import HeightExpandDiv from "../HeightExpandDiv";
import SimpleScrollBar from "../SimpleScrollBar";

import "simplebar-react/dist/simplebar.min.css";

export type SelectOption = {
  label: string;
  value: string;
};

type Props = {
  value?: string;
  renderLabel?: string;
  optionList: SelectOption[];
  onChange: (value: SelectOption) => void;
  defaultValue?: string;
  disabled?: boolean;
  className?: string;
  wrapperClassName?: string;
  isLoading?: boolean;
  unUsePadding?: boolean;
  arrowIcon?: React.ReactNode;
  optionClassName?: string;
  optionWrapperClassName?: string;
  prefixIcon?: React.ReactNode;
  optionWidth?: string;
  optionHeight?: string;
  optionOrder?: string;
  useTruncate?: boolean;
  additionalDiv?: React.ReactNode;
  onClick?: () => void;
  error?: string;
  unUseUniqueCheck?: boolean;
  variant?: "transparent" | "storeMenu" | "selectBuilding";
  labelClassName?: string;
  useError?: boolean;
  hideScrollbar?: boolean;
};

const CommonSelect: ForwardRefRenderFunction<HTMLDivElement, Props> = (
  {
    value,
    renderLabel,
    optionList,
    onChange,
    defaultValue,
    disabled,
    className,
    wrapperClassName,
    isLoading,
    arrowIcon,
    optionClassName,
    prefixIcon,
    optionWidth,
    optionHeight,
    additionalDiv,
    onClick,
    optionWrapperClassName,
    error,
    variant,
    labelClassName,
    useError = true,
    useTruncate = false,
    unUsePadding = false,
    hideScrollbar = false,
    unUseUniqueCheck = false,
    optionOrder = "right",
  },
  ref
) => {
  // 크롬 브라우저의 경우 가상 키보드가 활성화 됐을 때 뷰포트가 줄어들어, max-h-[30vh]로 높이를 계산하는 방식으로는 옵션 div 출력이 중간에서 잘리는 문제 발생.
  // 따라서 첫 렌더링될 당시의 전체 높이를 저장한 뒤 높이를 계산할 수 있도록 수정.
  // 수정 전: 가상 키보드가 활성화된 상태에서 select를 클릭 했을 때 줄어든 뷰포트로 계산된 높이로 옵션 영역이 표출되어 하단 옵션이 잘림.
  // 수정 후: 첫 렌더링 시 뷰포트 기본 높이를 저장한 뒤 해당 초기 뷰포트 높이값의 30%로 select 옵션 높이 계산.
  const [initialHeight, setInitialHeight] = useState<number>(
    window.innerHeight
  );

  const [isOpen, setIsOpen] = useState<boolean>(false);
  const divRef = useRef<HTMLDivElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [errorMessage, setErrorMessage] = useState<string>("");

  const handleScroll = (e: React.UIEvent<HTMLUListElement, UIEvent>) => {
    e.stopPropagation();
  };

  const handleTouchMove = (e: React.TouchEvent<HTMLUListElement>) => {
    e.stopPropagation();
  };

  const getDefaultArrowIcon = () => {
    switch (variant) {
      case "transparent":
      case "storeMenu":
        return <TransparentArrowIcon />;
      default:
        return <DefaultArrowIcon />;
    }
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // useEffect(() => {
  //   console.log("optionList");
  //   setIsOpen(false);
  //   setErrorMessage("");
  // }, [optionList]);

  useEffect(() => {
    if (error) setErrorMessage(error);
  }, [error]);

  return (
    <div
      className={clsx([
        "w-full relative h-fit",
        wrapperClassName,
        {
          "opacity-60":
            disabled || !optionList.length || optionList.length === 0,
          "flex justify-end w-fit": variant === "transparent",
          "w-fit": variant === "selectBuilding",
        },
      ])}
      ref={wrapperRef}
    >
      {isLoading && (
        <div className="absolute inset-0 bg-black bg-opacity-10 flex items-center justify-center z-30">
          <div className="h-[40px] w-[40px] border-2 border-neutral-500 border-t-brand-primary-magenta animate-spin rounded-full"></div>
        </div>
      )}
      <div
        className={clsx([
          // min-w-130px 제약은 핸드폰 번호 인증쪽에서 사용되는데, 해당 부분 수정 시 확인할 것
          "relative appearance-none focus:outline-none min-w-[110px] w-full text-base flex items-center",
          {
            "p-4": !unUsePadding,
            "border-brand-primary-magenta": errorMessage,
            "justify-end": optionOrder === "right",
            "justify-start": optionOrder === "left",
            "justify-between": optionOrder === "between",
            "border border-gray-300": !variant,
            "border-none text-right p-0 h-[40px] gap-1":
              variant === "transparent",
            " bg-brand-neutral-05 border-none rounded-[10px] w-fit h-[40px] gap-[15px]":
              variant === "storeMenu",
            "text-black border-b-2 border-black text-3xl gap-2":
              variant === "selectBuilding",
          },
          className,
        ])}
        onClick={() => {
          if (disabled) return;
          onClick?.();
          setErrorMessage("");

          if (
            !unUseUniqueCheck &&
            optionList.length === 1 &&
            optionList[0].value === value
          ) {
            setErrorMessage("선택할 수 있는 옵션이 없습니다.");
            return;
          }

          if (!disabled && optionList.length && optionList.length > 0) {
            setIsOpen((prev) => !prev);
          } else {
            console.log("오픈 실페");
          }
        }}
        ref={ref}
        data-testid="common-select-button"
      >
        <div className="flex items-center gap-1">
          {prefixIcon}
          <div
            className={clsx([
              {
                truncate: useTruncate,
                "max-w-[calc(100vw-100px)]": useTruncate && !labelClassName,
                "text-brand-neutral-50": !(value && value.length > 0),
              },
              labelClassName,
            ])}
          >
            {value && value.length > 0
              ? renderLabel
                ? renderLabel
                : optionList.find((item) => item.value === value)?.label
              : defaultValue}
          </div>
        </div>
        <div
          className={clsx([
            "pointer-events-none flex items-center text-gray-700 transition-all ",
            { "rotate-180": isOpen },
          ])}
        >
          {arrowIcon ? arrowIcon : getDefaultArrowIcon()}
        </div>
      </div>
      <div
        className={clsx([
          // 해당 className 수정 시, 건물층 선택과 게시판의 정렬 선택 박스도 확인할 것
          "absolute bg-white z-20 top-full mt-1 rounded w-full text-base",
          {
            "left-0": optionOrder === "left",
            "right-0": optionOrder === "right",
          },
          optionWrapperClassName,
        ])}
        style={{
          width: optionWidth ?? divRef.current?.getBoundingClientRect().width,
          boxShadow: "0px 5px 10px 0px rgba(0, 0, 0, 0.25)",
        }}
      >
        <HeightExpandDiv
          expand={isOpen}
          // innerClassName={optionHeight ? `h-[${optionHeight}]` : ""}
          style={{ width: optionWidth, maxHeight: optionHeight }}
        >
          <SimpleScrollBar
            className={clsx({ "hide-scrollbar": hideScrollbar })}
            style={{ maxHeight: optionHeight || initialHeight * 0.3 }}
            autoHide={false}
          >
            <ul
              onScroll={handleScroll}
              onTouchMove={handleTouchMove}
              className={clsx([
                optionClassName,
                { "py-2": !additionalDiv, "pt-2": additionalDiv },
              ])}
            >
              {optionList
                .filter((option) => option.value !== value)
                .map((option, index) => (
                  <li
                    key={index}
                    onClick={() => {
                      onChange(option);
                      setIsOpen(false);
                    }}
                    className={clsx([
                      "w-full py-2.5 px-4 cursor-pointer",
                      { truncate: useTruncate },
                    ])}
                  >
                    {option.label}
                  </li>
                ))}
            </ul>
          </SimpleScrollBar>
          {additionalDiv}
        </HeightExpandDiv>
      </div>
      {useError && errorMessage && (
        <div className="text-brand-primary-magenta text-xs px-4 pt-1">
          *{errorMessage}
        </div>
      )}
    </div>
  );
};

export const DefaultArrowIcon = () => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <g opacity="0.5">
      <path
        d="M6 9L12 15L18 9"
        stroke="black"
        strokeWidth="1.75"
        strokeLinecap="square"
        strokeLinejoin="round"
      />
    </g>
  </svg>
);

export const TransparentArrowIcon = () => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M6 9L12 15L18 9"
      stroke="black"
      strokeWidth="1.75"
      strokeLinecap="square"
      strokeLinejoin="round"
    />
  </svg>
);

export default React.forwardRef(CommonSelect);
